import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const OVERALL_SCORE_OPTIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'meets_expectation', label: 'Meets expectation' },
  { value: 'under_expectation', label: 'Under Expectation' },
];

function YesNoRow({ question, value, comments, onChange, onCommentsChange, disabled }) {
  return (
    <div className="border rounded-lg p-3 bg-white space-y-2">
      <p className="text-sm text-gray-900 leading-relaxed">{question.label}</p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={value === 'yes' ? 'default' : 'outline'}
          className={cn(value === 'yes' && 'bg-green-600 hover:bg-green-700')}
          onClick={() => !disabled && onChange('yes')}
          disabled={disabled}
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          YES
        </Button>
        <Button
          type="button"
          size="sm"
          variant={value === 'no' ? 'default' : 'outline'}
          className={cn(value === 'no' && 'bg-red-600 hover:bg-red-700')}
          onClick={() => !disabled && onChange('no')}
          disabled={disabled}
        >
          <XCircle className="h-3.5 w-3.5 mr-1" />
          NO
        </Button>
      </div>
      <Textarea
        placeholder="Evidence / comments (optional)"
        value={comments || ''}
        onChange={(e) => onCommentsChange(e.target.value)}
        disabled={disabled}
        rows={2}
        className="text-sm"
      />
    </div>
  );
}

function buildInitialFormData(template, employeeName, completedByName) {
  const data = { overall_score: '' };
  if (!template) return data;

  for (const field of template.header_fields || []) {
    if (field.prefill === 'employee_name') {
      data[field.id] = employeeName || '';
    } else if (field.id === 'completed_by_name' || field.id === 'supervisor_name') {
      data[field.id] = completedByName || '';
    } else if (field.type === 'date' && field.id === 'check_date') {
      data[field.id] = new Date().toISOString().split('T')[0];
    } else {
      data[field.id] = '';
    }
  }

  for (const section of template.sections || []) {
    if (section.type === 'yes_no_questions') {
      for (const q of section.questions || []) {
        data[q.id] = '';
        data[`${q.id}_comments`] = '';
      }
    }
    if (section.type === 'actions_table') {
      const rowCount = section.row_count || 4;
      for (let i = 0; i < rowCount; i += 1) {
        for (const col of section.columns || []) {
          data[`actions_${i}_${col.id}`] = '';
        }
      }
    }
    if (section.type === 'textarea') {
      data[section.field_id] = '';
    }
    if (section.type === 'signatures') {
      for (const field of section.fields || []) {
        data[field.id] = field.type === 'date' ? new Date().toISOString().split('T')[0] : '';
      }
    }
  }

  return data;
}

export default function StructuredSpotCheckForm({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  token,
  completedByName,
  onSuccess,
}) {
  const [step, setStep] = useState('choose');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setSelectedTemplate(null);
    setFormData({});
    fetchTemplates();
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/spot-check-form-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load spot check templates:', error);
      toast.error('Failed to load spot check forms');
    } finally {
      setLoading(false);
    }
  };

  const startTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData(buildInitialFormData(template, employeeName, completedByName));
    setStep('form');
  };

  const updateField = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const questionSections = useMemo(
    () => (selectedTemplate?.sections || []).filter((s) => s.type === 'yes_no_questions'),
    [selectedTemplate],
  );

  const answeredCount = useMemo(() => {
    if (!selectedTemplate) return { total: 0, answered: 0 };
    let total = 0;
    let answered = 0;
    for (const section of questionSections) {
      for (const q of section.questions || []) {
        total += 1;
        if (formData[q.id] === 'yes' || formData[q.id] === 'no') answered += 1;
      }
    }
    return { total, answered };
  }, [formData, questionSections, selectedTemplate]);

  const sectionSummary = useMemo(() => {
    if (!selectedTemplate) return { questionSections: 0, hasActions: false, hasSignatures: false };
    const sections = selectedTemplate.sections || [];
    return {
      questionSections: sections.filter((s) => s.type === 'yes_no_questions').length,
      hasActions: sections.some((s) => s.type === 'actions_table'),
      hasSignatures: sections.some((s) => s.type === 'signatures'),
    };
  }, [selectedTemplate]);

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    if (!formData.overall_score) {
      toast.error('Please select the overall scoring mark');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/spot-checks/structured`,
        {
          template_id: selectedTemplate.template_id,
          form_data: formData,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(`${selectedTemplate.title} saved`);
      onOpenChange(false);
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors?.length) {
        toast.error(detail.errors[0]);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error('Failed to save spot check form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderHeaderFields = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg border">
      {(selectedTemplate?.header_fields || []).map((field) => (
        <div key={field.id} className={field.type === 'radio' ? 'sm:col-span-2' : ''}>
          <Label className="text-xs text-gray-600">
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          {field.type === 'radio' ? (
            <RadioGroup
              value={formData[field.id] || ''}
              onValueChange={(v) => updateField(field.id, v)}
              className="flex flex-wrap gap-4 mt-1"
            >
              {(field.options || []).map((opt) => (
                <div key={opt.value} className="flex items-center gap-2">
                  <RadioGroupItem value={opt.value} id={`${field.id}-${opt.value}`} />
                  <Label htmlFor={`${field.id}-${opt.value}`} className="font-normal cursor-pointer">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          ) : (
            <Input
              type={field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
              value={formData[field.id] || ''}
              onChange={(e) => updateField(field.id, e.target.value)}
              className="mt-1"
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderFormBody = () => (
    <div className="space-y-6">
      <div className="p-4 border rounded-lg bg-white">
        <Label className="text-sm font-medium">
          Overall scoring mark on performance and care given <span className="text-red-500">*</span>
        </Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {OVERALL_SCORE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              type="button"
              size="sm"
              variant={formData.overall_score === opt.value ? 'default' : 'outline'}
              onClick={() => updateField('overall_score', opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {renderHeaderFields()}

      {(selectedTemplate?.sections || []).map((section) => {
        if (section.type === 'yes_no_questions') {
          return (
            <div key={section.id} className="space-y-3">
              <h3 className="font-semibold text-primary text-sm border-b pb-2">{section.title}</h3>
              <div className="space-y-2">
                {(section.questions || []).map((q) => (
                  <YesNoRow
                    key={q.id}
                    question={q}
                    value={formData[q.id]}
                    comments={formData[`${q.id}_comments`]}
                    onChange={(v) => updateField(q.id, v)}
                    onCommentsChange={(v) => updateField(`${q.id}_comments`, v)}
                  />
                ))}
              </div>
            </div>
          );
        }

        if (section.type === 'actions_table') {
          const rowCount = section.row_count || 4;
          return (
            <div key={section.id} className="space-y-3">
              <h3 className="font-semibold text-primary text-sm border-b pb-2">{section.title}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border p-2 w-8">#</th>
                      {(section.columns || []).map((col) => (
                        <th key={col.id} className="border p-2 text-left">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: rowCount }).map((_, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className="border p-2 text-center text-gray-500">{rowIdx + 1}</td>
                        {(section.columns || []).map((col) => (
                          <td key={col.id} className="border p-1">
                            <Input
                              value={formData[`actions_${rowIdx}_${col.id}`] || ''}
                              onChange={(e) => updateField(`actions_${rowIdx}_${col.id}`, e.target.value)}
                              className="h-8 text-xs border-0 shadow-none"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        if (section.type === 'textarea') {
          return (
            <div key={section.id} className="space-y-2">
              <Label className="font-medium text-sm">{section.title}</Label>
              <Textarea
                rows={4}
                value={formData[section.field_id] || ''}
                onChange={(e) => updateField(section.field_id, e.target.value)}
                placeholder="Describe key strengths demonstrated..."
              />
            </div>
          );
        }

        if (section.type === 'signatures') {
          return (
            <div key={section.id} className="space-y-3 p-4 bg-slate-50 rounded-lg border">
              <h3 className="font-semibold text-sm">{section.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(section.fields || []).map((field) => (
                  <div key={field.id}>
                    <Label className="text-xs text-gray-600">{field.label}</Label>
                    <Input
                      type={field.type === 'date' ? 'date' : 'text'}
                      value={formData[field.id] || ''}
                      onChange={(e) => updateField(field.id, e.target.value)}
                      placeholder={field.type === 'date' ? '' : 'Type name to sign'}
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return null;
      })}

      <div className="text-center text-xs text-gray-400 py-4 border-t mt-6">
        End of form — all sections above. Review answers, then click Save &amp; generate PDF.
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] max-h-[90vh] !flex flex-col overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            {step === 'choose' ? 'Start spot check' : selectedTemplate?.title}
          </DialogTitle>
          <DialogDescription>
            {step === 'choose'
              ? 'Choose the approved workspace form. Complete all YES/NO sections, then save to generate a branded PDF.'
              : `Answer all questions (${answeredCount.answered}/${answeredCount.total} completed) — scroll down for all sections, actions and signatures`}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' ? (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl.template_id}
                    type="button"
                    onClick={() => startTemplate(tpl)}
                    className="text-left p-4 rounded-xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors"
                    data-testid={`start-spot-check-${tpl.template_id}`}
                  >
                    <p className="font-semibold text-gray-900">{tpl.title}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Full YES/NO sections matching the approved workspace template
                    </p>
                    <Badge variant="outline" className="mt-3">Official form</Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="shrink-0 px-6 py-2 border-b bg-slate-50 text-xs text-gray-600 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                <strong>{sectionSummary.questionSections}</strong> YES/NO sections
              </span>
              <span>
                <strong>{answeredCount.total}</strong> questions
              </span>
              {sectionSummary.hasActions && <span>Actions table</span>}
              {sectionSummary.hasSignatures && <span>Signatures</span>}
              <span className="text-primary font-medium">Scroll down ↓</span>
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4"
              data-testid="structured-spot-check-scroll"
            >
              {renderFormBody()}
            </div>
            <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 sm:gap-2 bg-white">
              <Button variant="outline" onClick={() => setStep('choose')} disabled={submitting}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="save-structured-spot-check">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save &amp; generate PDF
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
